import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { TableArtifact } from "./types";

type View = "collapsed" | "expanded";

export function TableArtifactView({ artifact }: { artifact: TableArtifact }) {
  const [view, setView] = useState<View>("collapsed");
  const [modal, setModal] = useState(false);
  const rows = artifact.rows ?? [];
  const columns = useMemo<ColumnDef<unknown[]>[]>(
    () =>
      artifact.columns.map((c, i) => ({
        id: c.name,
        header: c.name,
        accessorFn: (row) => row[i],
      })),
    [artifact.columns],
  );
  const visibleRows = view === "collapsed" ? rows.slice(0, 1) : rows;
  const hiddenCount = Math.max(0, rows.length - visibleRows.length);

  const downloadCsv = () => {
    const header = artifact.columns.map((c) => c.name).join(",");
    const body = rows.map((r) => r.map((v) => csvCell(v)).join(",")).join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "table.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="artifact artifact--table">
      {view === "collapsed" && rows.length > 1 ? (
        <button
          type="button"
          className="artifact-table-collapsed"
          onClick={() => setView("expanded")}
          title="Click to expand"
        >
          <div className="artifact-table-scroll">
            <TableInner rows={visibleRows} columns={columns} />
          </div>
          <div className="artifact-table-more">+ {hiddenCount} more rows · click to expand</div>
        </button>
      ) : (
        <div className="artifact-table-scroll">
          <TableInner rows={visibleRows} columns={columns} />
        </div>
      )}
      {view === "expanded" && (
        <div className="artifact-actions no-print">
          {rows.length > 1 && (
            <button type="button" onClick={() => setView("collapsed")}>
              Collapse
            </button>
          )}
          <button type="button" onClick={() => setModal(true)} disabled={rows.length === 0}>
            Expand
          </button>
          <button type="button" onClick={downloadCsv} disabled={rows.length === 0}>
            Download CSV
          </button>
        </div>
      )}
      {modal && (
        <TableOverlay onClose={() => setModal(false)}>
          <TableInner rows={rows} columns={columns} />
        </TableOverlay>
      )}
    </div>
  );
}

function TableInner({ rows, columns }: { rows: unknown[][]; columns: ColumnDef<unknown[]>[] }) {
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <table className="artifact-table">
      <thead>
        {table.getHeaderGroups().map((hg) => (
          <tr key={hg.id}>
            {hg.headers.map((h) => (
              <th key={h.id}>{flexRender(h.column.columnDef.header, h.getContext())}</th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((r) => (
          <tr key={r.id}>
            {r.getVisibleCells().map((c) => (
              <td key={c.id}>
                <div className="artifact-cell">
                  {flexRender(c.column.columnDef.cell, c.getContext())}
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TableOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);
  return createPortal(
    <div
      className="table-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="table-overlay-panel">
        <button
          type="button"
          className="table-overlay-close"
          onClick={onClose}
          aria-label="Close expanded table"
        >
          ×
        </button>
        <div className="table-overlay-scroll">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

function csvCell(v: unknown): string {
  const s = v == null ? "" : String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
